
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Package,
  X,
  RefreshCw,
  Star,
  Calendar,
  IndianRupee,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  Truck,
  LogOut,
  Eye,
  ShoppingBag,
} from "lucide-react";
import { ordersApi, reviewsApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";
import toast from "react-hot-toast";
import { getSmartProductPlaceholder } from "@/lib/placeholders";
// Image removed - use img tag;

interface Order {
  id: number;
  order_number: string;
  razorpay_order_id?: string | null;
  status: string;
  payment_status: string;
  total_amount: number;
  created_at: string;
  items: OrderItem[];
  address?: {
    name: string;
    phone: string;
    address_line1: string;
    address_line2?: string | null;
    city: string;
    state: string;
    pincode: string;
    country: string;
  } | null;
  timeline?: Array<{
    id: number;
    status: string;
    description: string;
    created_at: string;
  }>;
}

interface OrderItem {
  id: number;
  product_id: number;
  product_name: string;
  product_image_url?: string | null;
  quantity: number;
  price: number;
  subtotal: number;
}

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, logout } = useAuthStore();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [reviewData, setReviewData] = useState({
    product_id: 0,
    order_id: 0,
    rating: 5,
    title: "",
    comment: "",
  });

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setShowOTPDialog(true);
      return;
    }
    fetchOrders();
  }, [isAuthenticated, token]);

  const handleOTPSuccess = () => {
    setShowOTPDialog(false);
    fetchOrders();
  };

  const fetchOrders = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await ordersApi.getAll(token);
      // ordersApi.getAll already unwraps the response, so response is { total, orders }
      const ordersList = Array.isArray(response) ? response : (response?.orders || []);
      setOrders(ordersList);
    } catch (error: any) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
      setOrders([]); // Ensure orders is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder || !token || !cancelReason.trim()) return;
    try {
      setCancelling(true);
      await ordersApi.cancel(selectedOrder.id, token, cancelReason);
      toast.success("Order cancellation request submitted successfully");
      setShowCancelModal(false);
      setCancelReason("");
      // Refresh orders to get updated status
      const updatedOrders = await ordersApi.getAll(token);
      const ordersList = Array.isArray(updatedOrders) ? updatedOrders : (updatedOrders?.orders || []);
      setOrders(ordersList);
      
      // Update selected order if dialog is still open
      if (showOrderDetails) {
        const updatedOrder = ordersList.find((o: Order) => o.id === selectedOrder.id);
        if (updatedOrder) {
          setSelectedOrder(updatedOrder);
        }
      } else {
        setSelectedOrder(null);
      }
    } catch (error: any) {
      console.error("Error cancelling order:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleRequestRefund = async () => {
    if (!selectedOrder || !token) return;
    try {
      await ordersApi.requestRefund(selectedOrder.id, token, refundReason);
      toast.success("Refund request submitted successfully");
      setShowRefundModal(false);
      setRefundReason("");
      setSelectedOrder(null);
      fetchOrders();
    } catch (error: any) {
      console.error("Error requesting refund:", error);
      toast.error(error.response?.data?.detail || "Failed to request refund");
    }
  };

  const handleSubmitReview = async () => {
    if (!token) return;
    try {
      await reviewsApi.create(reviewData, token);
      toast.success("Review submitted successfully");
      setShowReviewModal(false);
      setReviewData({
        product_id: 0,
        order_id: 0,
        rating: 5,
        title: "",
        comment: "",
      });
    } catch (error: any) {
      console.error("Error submitting review:", error);
      toast.error(error.response?.data?.detail || "Failed to submit review");
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    // Redirect to home page with full page reload for clean state
    setTimeout(() => {
      window.location.href = "/";
    }, 100);
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return <CheckCircle className="w-3 h-3 text-green-600" />;
      case "cancelled":
        return <XCircle className="w-3 h-3 text-red-600" />;
      case "shipped":
        return <Truck className="w-3 h-3 text-blue-600" />;
      default:
        return <Clock className="w-3 h-3 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "bg-green-50 text-green-700 border-green-200";
      case "cancelled":
        return "bg-red-50 text-red-700 border-red-200";
      case "shipped":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "confirmed":
        return "bg-purple-50 text-purple-700 border-purple-200";
      default:
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <OTPLoginDialog 
        isOpen={showOTPDialog} 
        onClose={() => setShowOTPDialog(false)} 
        onSuccess={handleOTPSuccess} 
      />
      {!isAuthenticated ? null : (
        <>
          <main className="flex-grow pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                  My Account
                </h1>
                <p className="text-sm text-gray-500">
                  Welcome back, {user?.first_name || user?.email}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>
          </motion.div>

          {/* Orders Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-500" />
                Orders
              </h2>
            </div>

            <div className="p-5">
              {loading ? (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900 mx-auto"></div>
                  <p className="mt-3 text-sm text-gray-500">Loading orders...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-16">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-900 mb-1">No orders yet</p>
                  <p className="text-xs text-gray-500 mb-5">Start shopping to see your orders here</p>
                  <Link
                    to="/products"
                    className="inline-block px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
                  >
                    Browse Products
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {Array.isArray(orders) && orders.length > 0 ? (
                    orders.map((order) => {
                      const itemsCount = order.items?.length || 0;
                      return (
                        <motion.div
                          key={order.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          onClick={() => handleOrderClick(order)}
                          className="border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer bg-white"
                        >
                          <div className="px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <div className="flex-shrink-0">
                                <Package className="w-5 h-5 text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-gray-900">#{order.order_number}</span>
                                  <span
                                    className={`px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wide flex items-center gap-1 ${getStatusColor(
                                      order.status
                                    )}`}
                                  >
                                    {getStatusIcon(order.status)}
                                    {order.status}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                  </div>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <ShoppingBag className="w-3 h-3" />
                                    {itemsCount} {itemsCount === 1 ? "item" : "items"}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="text-right">
                                  <p className="text-sm font-semibold text-gray-900">₹{order.total_amount.toLocaleString()}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-600">No orders found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

        {/* Order Details Dialog */}
        <AnimatePresence>
          {showOrderDetails && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowOrderDetails(false);
              setSelectedOrder(null);
            }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Order Details</h2>
                  <p className="text-sm text-gray-500 mt-0.5">#{selectedOrder.order_number}</p>
                </div>
                <button
                  onClick={() => {
                    setShowOrderDetails(false);
                    setSelectedOrder(null);
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-5">
                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Status</p>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wide flex items-center gap-1 ${getStatusColor(
                            selectedOrder.status
                          )}`}
                        >
                          {getStatusIcon(selectedOrder.status)}
                          {selectedOrder.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Date Ordered</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(selectedOrder.created_at).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                      <p className="text-sm font-semibold text-gray-900">₹{selectedOrder.total_amount.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Items</p>
                      <p className="text-sm font-medium text-gray-900">{selectedOrder.items?.length || 0} items</p>
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Products
                  </h3>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg"
                      >
                        <div className="relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={item.product_image_url || getSmartProductPlaceholder(item.product_name, "product")}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = getSmartProductPlaceholder(item.product_name, "product");
                            }}
                          />
                        </div>
                        <div className="flex-grow min-w-0">
                          <Link
                            to={`/products/${item.product_id}`}
                            className="text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors line-clamp-1"
                          >
                            {item.product_name}
                          </Link>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Quantity: {item.quantity} × ₹{item.price.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-gray-900">₹{item.subtotal.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tracking Timeline */}
                {selectedOrder.timeline && selectedOrder.timeline.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Order Timeline
                    </h3>
                    <div className="space-y-3 relative">
                      {selectedOrder.timeline.map((entry: any, index: number) => {
                        const isLast = index === selectedOrder.timeline!.length - 1;
                        const statusColors: Record<string, string> = {
                          pending: "bg-yellow-500",
                          confirmed: "bg-blue-500",
                          processing: "bg-purple-500",
                          shipped: "bg-indigo-500",
                          delivered: "bg-green-500",
                          cancelled: "bg-red-500",
                          payment_completed: "bg-green-500",
                          payment_failed: "bg-red-500",
                        };
                        const statusColor = statusColors[entry.status] || "bg-gray-500";

                        return (
                          <div key={entry.id} className="flex items-start gap-3 relative">
                            <div className="relative flex flex-col items-center">
                              <div className={`w-2.5 h-2.5 ${statusColor} rounded-full z-10`} />
                              {!isLast && (
                                <div className="absolute top-2.5 left-1/2 transform -translate-x-1/2 w-0.5 h-8 bg-gray-200" />
                              )}
                            </div>
                            <div className="flex-1 pb-3">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-medium text-gray-900 capitalize">{entry.status.replace("_", " ")}</p>
                                {isLast && <CheckCircle className="w-3 h-3 text-green-500" />}
                              </div>
                              {entry.description && (
                                <p className="text-xs text-gray-500 mt-0.5">{entry.description}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(entry.created_at).toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex gap-2">
                    {selectedOrder.status.toLowerCase() !== "cancelled" &&
                      selectedOrder.status.toLowerCase() !== "delivered" &&
                      selectedOrder.status.toLowerCase() !== "shipped" && (
                        <button
                          onClick={() => {
                            setShowOrderDetails(false);
                            setShowCancelModal(true);
                          }}
                          className="flex-1 px-4 py-2.5 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Cancel Order
                        </button>
                      )}
                    {selectedOrder.status.toLowerCase() === "delivered" && (
                      <button
                        onClick={() => {
                          setShowOrderDetails(false);
                          setShowRefundModal(true);
                        }}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-orange-600 border border-orange-200 rounded-md hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Request Refund
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>

        {/* Cancel Order Modal */}
        <AnimatePresence>
          {showCancelModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCancelModal(false)}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl p-5 max-w-md w-full"
            >
              <h3 className="text-base font-semibold text-gray-900 mb-2">Cancel Order</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to cancel order #{selectedOrder.order_number}? Please provide a reason for cancellation.
              </p>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  Reason for Cancellation <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please provide a reason for cancellation..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  rows={4}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="w-3 h-3 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span>I would like to request a refund for this order</span>
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason("");
                    setSelectedOrder(null);
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={cancelling}
                >
                  Close
                </button>
                <button
                  onClick={handleCancelOrder}
                  disabled={cancelling || !cancelReason.trim()}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {cancelling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Cancellation Request"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>

        {/* Refund Modal */}
        <AnimatePresence>
          {showRefundModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowRefundModal(false)}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl p-5 max-w-md w-full"
            >
              <h3 className="text-base font-semibold text-gray-900 mb-2">Request Refund</h3>
              <p className="text-sm text-gray-600 mb-4">
                Request a refund for order #{selectedOrder.order_number}
              </p>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Reason for refund (optional)"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent mb-4"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowRefundModal(false);
                    setRefundReason("");
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestRefund}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                >
                  Submit
                </button>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>

        {/* Review Modal */}
        <AnimatePresence>
          {showReviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReviewModal(false)}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl p-5 max-w-md w-full"
            >
              <h3 className="text-base font-semibold text-gray-900 mb-4">Write a Review</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Rating
                  </label>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setReviewData({ ...reviewData, rating })}
                        className={`p-1.5 rounded transition-colors ${
                          reviewData.rating >= rating
                            ? "text-yellow-500"
                            : "text-gray-300"
                        }`}
                      >
                        <Star
                          className={`w-4 h-4 ${
                            reviewData.rating >= rating ? "fill-current" : ""
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Title (optional)
                  </label>
                  <input
                    type="text"
                    value={reviewData.title}
                    onChange={(e) =>
                      setReviewData({ ...reviewData, title: e.target.value })
                    }
                    placeholder="Review title"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Comment
                  </label>
                  <textarea
                    value={reviewData.comment}
                    onChange={(e) =>
                      setReviewData({ ...reviewData, comment: e.target.value })
                    }
                    placeholder="Share your experience..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    rows={4}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setReviewData({
                      product_id: 0,
                      order_id: 0,
                      rating: 5,
                      title: "",
                      comment: "",
                    });
                  }}
                  className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReview}
                  className="flex-1 px-3 py-2 text-sm font-medium bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  Submit
                </button>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>
        </>
      )}
    </div>
  );
}
